import type { ReactNode } from 'react';
import { Link } from 'react-router';
import type { SessionSummary } from '@erectus/shared';
import { Card, SectionTitle } from '@/ui/primitives.js';
import { Icon } from '@/ui/icons.js';
import { scoreColor } from '@/ui/score.js';

type Session = SessionSummary & { id: string };

/**
 * Every recorded session, newest first — the plain way into reports. The
 * calendar view lives in the Activity card at the top of Progress, alongside
 * exercises.
 */
export function SessionHistory({ sessions }: { sessions: readonly Session[] }): ReactNode {
  return (
    <Card className="space-y-1">
      <SectionTitle>Sessions</SectionTitle>
      <ul className="divide-y divide-line">
        {sessions.map((session) => {
          const start = new Date(session.startedAt);
          return (
            <li key={session.id}>
              <Link to={`/report/${session.id}`} state={{ from: 'progress' }} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                <span className="min-w-0">
                  <span className="block font-medium text-ink">
                    {start.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                  </span>
                  <span className="block text-xs text-muted">
                    {start.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })} ·{' '}
                    {Math.round(session.durationSec / 60)} min
                  </span>
                </span>
                <span className="flex items-center gap-2">
                  <span className="text-base font-bold" style={{ color: scoreColor(session.score) }}>
                    {session.score}
                  </span>
                  <Icon name="back" size={16} className="rotate-180 text-muted" />
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
